import axios from "axios";
import { CASE_TYPES, RULE_TYPE } from "./constants";
import {
  formatIsoDateForInput,
  getCampaignBudgetCurrencyCode,
  getMaxBudgetError,
  getMinBudgetError,
  getMaxCampaignBid,
  getMinCampaignBudget,
  isMaxBudgetValid,
  isMinBudgetValid,
} from "./helpers";
import { formatSpBidBound } from "./bidCapsConfig";

/** Validate rule campaigns/schedules for conflicts before activating (list page). */
export async function fetchCampaignScheduleConflictValidation({
  clientId,
  userPlatform = "amazon",
  campaignIds = [],
  scheduleStartDate,
  scheduleEndDate,
  excludeRuleSetId,
}) {
  const start = formatIsoDateForInput(scheduleStartDate);
  const end = formatIsoDateForInput(scheduleEndDate);
  const ids = [...new Set((campaignIds ?? []).map(String).filter(Boolean))];

  if (!clientId) {
    return {
      valid: false,
      issues: [{ issue_type: "CAMPAIGN_SCHEDULE_CONFLICT", message: "Client is not selected." }],
    };
  }
  if (!ids.length || !start || !end) {
    return {
      valid: false,
      issues: [
        {
          issue_type: "CAMPAIGN_SCHEDULE_CONFLICT",
          message:
            "This rule is missing campaigns or schedule dates required to validate activation.",
        },
      ],
    };
  }

  try {
    const { data } = await axios.post(
      `${process.env.REACT_APP_ADS_API}/client/${clientId}/automation-rule-set:validate-rule?source=${userPlatform}&type=${RULE_TYPE}`,
      {
        issue_types: ["CAMPAIGN_SCHEDULE_CONFLICT"],
        campaign_ids: ids,
        schedule_config: {
          start_date: start,
          end_date: end,
        },
        ...(excludeRuleSetId ? { exclude_rule_set_id: excludeRuleSetId } : {}),
      }
    );

    const result = data?.data ?? data ?? {};
    const issues = Array.isArray(result.issues) ? result.issues : [];
    const scheduleIssues = issues.filter((i) => i?.issue_type === "CAMPAIGN_SCHEDULE_CONFLICT");

    return {
      valid: scheduleIssues.length === 0,
      issues: scheduleIssues,
    };
  } catch (error) {
    const message =
      error?.response?.data?.message ??
      error?.response?.data?.error ??
      error?.message ??
      "Failed to validate rule schedule.";
    return {
      valid: false,
      issues: [{ issue_type: "CAMPAIGN_SCHEDULE_CONFLICT", message }],
    };
  }
}

function parseTimeToMinutes(timeStr) {
  if (timeStr == null) return null;
  const s = String(timeStr).trim();
  const m = s.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  if (Number.isNaN(h) || Number.isNaN(min) || min < 0 || min > 59) {
    return null;
  }
  if (h === 24 && min === 0) return 24 * 60;
  if (h < 0 || h > 23) return null;
  return h * 60 + min;
}

function getDaypartingSlotErrors(slots) {
  const errors = {};
  const slotEntries = (slots ?? []).map((slot, index) => ({ slot, index }));

  if (slotEntries.length === 0) return errors;

  const parsed = slotEntries.map(({ slot, index }) => ({
    localId: slot.localId,
    startMin: parseTimeToMinutes(slot.startTime),
    endMin: parseTimeToMinutes(slot.endTime),
    index,
  }));

  for (const { startMin, endMin, index, localId } of parsed) {
    if (startMin == null || endMin == null || startMin === "" || endMin === "") {
      errors[localId] = `Select start or end time (duration ${index + 1}).`;
    } else if (endMin <= startMin) {
      errors[localId] = `End time must be after start time (duration ${index + 1}).`;
    }
  }

  for (let i = 0; i < parsed.length; i++) {
    for (let j = i + 1; j < parsed.length; j++) {
      const a = parsed[i];
      const b = parsed[j];
      if (a.startMin == null || a.endMin == null || b.startMin == null || b.endMin == null) {
        continue;
      }
      if (a.startMin < b.endMin && b.startMin < a.endMin) {
        const msg = `Time slots cannot overlap between duration ${a.index + 1} and ${b.index + 1}.`;
        errors[a.localId] = msg;
        errors[b.localId] = msg;
      }
    }
  }

  return errors;
}

function getSlotDateRangeErrors(form) {
  const errors = {};
  const { scheduleStartDate, scheduleEndDate, slots } = form;
  const slotEntries = (slots ?? []).map((slot, index) => ({ slot, index }));

  slotEntries.forEach(({ slot, index }) => {
    const slotNum = index + 1;
    const { localId } = slot;

    if (!slot.startDate || !slot.endDate) {
      errors[localId] = `Duration ${slotNum}: start and end dates are required.`;
      return;
    }

    if (slot.startDate > slot.endDate) {
      errors[localId] = `Duration ${slotNum}: end date must be on or after start date.`;
      return;
    }

    if (scheduleStartDate && slot.startDate < scheduleStartDate) {
      errors[localId] = `Duration ${slotNum}: start date must be on or after rule start date (${scheduleStartDate}).`;
      return;
    }

    if (scheduleEndDate && slot.endDate > scheduleEndDate) {
      errors[localId] = `Duration ${slotNum}: end date must be on or before rule end date (${scheduleEndDate}).`;
    }
  });

  for (let i = 0; i < slotEntries.length; i++) {
    for (let j = i + 1; j < slotEntries.length; j++) {
      const a = slotEntries[i].slot;
      const b = slotEntries[j].slot;
      if (!a.startDate || !a.endDate || !b.startDate || !b.endDate) continue;
      if (a.startDate <= b.endDate && b.startDate <= a.endDate) {
        const slotNumA = slotEntries[i].index + 1;
        const slotNumB = slotEntries[j].index + 1;
        const msg = `Slot durations cannot overlap between duration ${slotNumA} and ${slotNumB}.`;
        if (!errors[a.localId]) errors[a.localId] = msg;
        if (!errors[b.localId]) errors[b.localId] = msg;
      }
    }
  }

  return errors;
}

/** Per-slot duration validation messages keyed by slot localId. */
export function getSlotDurationErrors(form) {
  if (!form) return {};
  if (form.automationMode === "DAYPARTING") {
    return getDaypartingSlotErrors(form.slots);
  }
  return getSlotDateRangeErrors(form);
}

export function validateForm(form) {
  if (!form.ruleName?.trim()) {
    return "Rule name is required.";
  }
  if (!form.selectedCampaignIds?.length) {
    return "Select at least one campaign.";
  }
  if (!form.scheduleStartDate || !form.scheduleEndDate) {
    return "Select schedule start and end dates.";
  }
  if (form.scheduleStartDate > form.scheduleEndDate) {
    return "Rule end date must be on or after rule start date.";
  }
  if (!form.selectedDays?.length) {
    return "Select at least one day for the schedule.";
  }

  if (form.targetingExclusion?.enabled) {
    if (!form.targetingExclusion.automatic && !form.targetingExclusion.broad) {
      return "Select at least one targeting exclusion (Automatic or Broad Keyword).";
    }
  }

  const slotErrors = getSlotDurationErrors(form);
  const firstSlotError = Object.values(slotErrors)[0];
  if (firstSlotError) return firstSlotError;

  const currency = getCampaignBudgetCurrencyCode();
  const minBound = formatSpBidBound(getMinCampaignBudget());
  const maxBound = formatSpBidBound(getMaxCampaignBid());
  const rangeHint = `${minBound}–${maxBound} ${currency}`;

  for (const mid of form.selectedCampaignIds ?? []) {
    const name = form.campaignMeta?.[mid]?.name ?? "Campaign";
    const minVal = form.campaignTableBids?.[mid]?.min_value;
    if (!isMinBudgetValid(minVal)) {
      const err =
        getMinBudgetError(minVal) ||
        (minVal === "" || minVal == null
          ? `Lower bid cap is required (SP range ${rangeHint})`
          : `Lower bid cap must be within SP range ${rangeHint}`);
      return `${name}: ${err}`;
    }

    const maxVal = form.campaignTableBids?.[mid]?.max_value;
    if (!isMaxBudgetValid(maxVal)) {
      const err =
        getMaxBudgetError(maxVal) ||
        (maxVal === "" || maxVal == null
          ? `Upper bid cap is required (SP range ${rangeHint})`
          : `Upper bid cap must be within SP range ${rangeHint}`);
      return `${name}: ${err}`;
    }

    const minNum = Number(minVal);
    const maxNum = Number(maxVal);
    if (!Number.isNaN(minNum) && !Number.isNaN(maxNum) && maxNum < minNum) {
      return `${name}: Upper bid cap must be greater than or equal to lower bid cap.`;
    }
  }

  let totalCases = 0;
  for (const [slotIndex, slot] of (form.slots ?? []).entries()) {
    const slotNum = slotIndex + 1;
    const increase = slot.cases?.[CASE_TYPES.INCREASE] ?? [];
    const decrease = slot.cases?.[CASE_TYPES.DECREASE] ?? [];
    const pause = slot.cases?.[CASE_TYPES.PAUSE] ?? [];
    totalCases += increase.length + decrease.length + pause.length;

    const validatePctCases = (list, label) => {
      for (const [i, caseItem] of list.entries()) {
        const pct = caseItem.percentageChange;
        if (pct === "" || pct == null || Number.isNaN(Number(pct))) {
          return `Duration ${slotNum} ${label} ${i + 1}: enter a bid % value.`;
        }
        if (caseItem.matchTypeBids?.enabled) {
          const rows = ["exact", "phrase", "broad"].map((k) => caseItem.matchTypeBids[k]);
          const anyChecked = rows.some((r) => r?.enabled);
          if (!anyChecked) {
            return `Duration ${slotNum} ${label} ${i + 1}: enable at least one match type or turn the option off.`;
          }
          for (const [key, row] of Object.entries({
            exact: caseItem.matchTypeBids.exact,
            phrase: caseItem.matchTypeBids.phrase,
            broad: caseItem.matchTypeBids.broad,
          })) {
            if (!row?.enabled) continue;
            if (row.value === "" || row.value == null || Number.isNaN(Number(row.value))) {
              return `Duration ${slotNum} ${label} ${i + 1}: enter ${key} match-type %.`;
            }
          }
        }
      }
      return "";
    };

    const incErr = validatePctCases(increase, "Increase Case");
    if (incErr) return incErr;
    const decErr = validatePctCases(decrease, "Decrease Case");
    if (decErr) return decErr;
  }

  if (totalCases === 0) {
    return "Add at least one Increase, Decrease, or Pause case in a slot.";
  }

  return "";
}
